import type { FetchXhrLogEntry } from '../types/console-log.types.js';

export const FETCH_LOG_EVENT = 'console-log-fetch';

const BODY_MAX = 8_000;
let _idSeq = 0;
let _intercepted = false;

function nextID(): string {
  return `fx-${Date.now()}-${++_idSeq}`;
}

function clampText(s: string): string {
  return s.length > BODY_MAX ? s.slice(0, BODY_MAX) + ' …[truncated]' : s;
}

function emit(entry: FetchXhrLogEntry): void {
  try { window.dispatchEvent(new CustomEvent(FETCH_LOG_EVENT, { detail: entry })); } catch { /* never break app */ }
}

function safeText(b: BodyInit | null | undefined): string | undefined {
  if (b == null) return undefined;
  try { return clampText(typeof b === 'string' ? b : JSON.stringify(b)); } catch { return undefined; }
}

/**
 * Singleton that patches window.fetch and XMLHttpRequest once.
 * Dispatches FETCH_LOG_EVENT CustomEvents for every request that completes or errors.
 * Safe to call multiple times — second call is a no-op.
 */
export function initFetchInterceptor(): void {
  if (_intercepted || typeof window === 'undefined') return;
  _intercepted = true;

  // ── window.fetch ──────────────────────────────────────────────────────────
  const _origFetch = (window.fetch as typeof window.fetch).bind(window);
  window.fetch = async (input: RequestInfo | URL, init?: RequestInit): Promise<Response> => {
    const id = nextID();
    const method = (init?.method ?? (input instanceof Request ? input.method : 'GET')).toUpperCase();
    const url =
      input instanceof URL ? input.toString()
      : input instanceof Request ? input.url
      : String(input);
    const t0 = Date.now();
    const reqBody = safeText(init?.body as BodyInit | null | undefined);

    try {
      const response = await _origFetch(input, init);
      let respBody: string | undefined;
      try { respBody = clampText(await response.clone().text()); } catch { /* ignore */ }
      emit({
        _type: 'fetch-xhr', id,
        timestamp: new Date().toISOString(),
        transport: 'fetch', method, url,
        status: response.status, statusText: response.statusText,
        durationMs: Date.now() - t0,
        requestBody: reqBody, responseBody: respBody,
        phase: response.ok ? 'complete' : 'error',
      });
      return response;
    } catch (err) {
      emit({
        _type: 'fetch-xhr', id,
        timestamp: new Date().toISOString(),
        transport: 'fetch', method, url,
        durationMs: Date.now() - t0,
        requestBody: reqBody,
        error: err instanceof Error ? err.message : String(err),
        phase: 'error',
      });
      throw err;
    }
  };

  // ── XMLHttpRequest ────────────────────────────────────────────────────────
  const OrigXHR = window.XMLHttpRequest;

  class PatchedXHR extends OrigXHR {
    private _id = nextID();
    private _method = 'GET';
    private _url = '';
    private _t0 = 0;
    private _reqBody: string | undefined;

    override open(method: string, url: string | URL, async = true, user?: string | null, pass?: string | null): void {
      this._method = method.toUpperCase();
      this._url = typeof url === 'string' ? url : url.toString();
      super.open(method, this._url, async, user ?? undefined, pass ?? undefined);
    }

    override send(body?: Document | XMLHttpRequestBodyInit | null): void {
      this._t0 = Date.now();
      this._reqBody = safeText(body as BodyInit | null | undefined);

      this.addEventListener('loadend', () => {
        let respBody: string | undefined;
        try { respBody = clampText(this.responseText); } catch { /* ignore */ }
        emit({
          _type: 'fetch-xhr',
          id: this._id,
          timestamp: new Date().toISOString(),
          transport: 'xhr',
          method: this._method, url: this._url,
          requestBody: this._reqBody,
          status: this.status, statusText: this.statusText,
          durationMs: Date.now() - this._t0,
          responseBody: respBody,
          phase: this.status === 0 || this.status >= 400 ? 'error' : 'complete',
          error: this.status === 0 ? 'Network error or request aborted' : undefined,
        });
      });

      super.send(body ?? undefined);
    }
  }

  (window as typeof window & { XMLHttpRequest: typeof XMLHttpRequest }).XMLHttpRequest =
    PatchedXHR as unknown as typeof XMLHttpRequest;
}
